import { apiClient } from '../apiClient';

export interface Attribution {
  src?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
  capturedAt?: string;
}

const STORAGE_KEY = 'vediluxe_attribution';
const MAX_AGE_DAYS = 30;

const clip = (value: string | null, max: number): string | undefined => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
};

/** Lowercase and strip to the charset the /track/visit endpoint accepts. */
const toTrackingKey = (value: string | undefined): string | undefined => {
  const key = (value || '').toLowerCase().replace(/[^a-z0-9._-]/g, '');
  return key || undefined;
};

/**
 * Reads ?src= and utm_* params from the landing URL and persists them
 * (last-touch wins) so checkout can attach them to the order. Call once
 * at app boot, before the router mounts.
 */
export const captureAttribution = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = clip(params.get('src'), 64)?.toLowerCase();
    const attribution: Attribution = {
      src,
      utmSource: clip(params.get('utm_source'), 128),
      utmMedium: clip(params.get('utm_medium'), 128),
      utmCampaign: clip(params.get('utm_campaign'), 128),
      utmTerm: clip(params.get('utm_term'), 128),
      utmContent: clip(params.get('utm_content'), 128),
      referrer: clip(document.referrer, 512),
      landingPage: clip(window.location.pathname + window.location.search, 512),
      capturedAt: new Date().toISOString()
    };

    // Only overwrite stored attribution when this pageview carries campaign
    // params — plain visits keep the original touch.
    const hasCampaign = Boolean(
      src || attribution.utmSource || attribution.utmMedium || attribution.utmCampaign
    );
    if (!hasCampaign) return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));

    // Fire-and-forget visit counter — ?src= wins, else utm_source;
    // utm_campaign rides along for the per-campaign breakdown
    const trackSrc = toTrackingKey(src) || toTrackingKey(attribution.utmSource);
    if (trackSrc) {
      const campaign = toTrackingKey(attribution.utmCampaign);
      apiClient
        .post('/track/visit', { src: trackSrc, ...(campaign ? { campaign } : {}) })
        .catch(() => {});
    }
  } catch {
    // Attribution must never break the storefront
  }
};

/** Stored attribution for order payloads, or undefined if absent/stale. */
export const getStoredAttribution = (): Attribution | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const attribution: Attribution = JSON.parse(raw);
    if (attribution.capturedAt) {
      const ageMs = Date.now() - new Date(attribution.capturedAt).getTime();
      if (ageMs > MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
        return undefined;
      }
    }
    return attribution;
  } catch {
    return undefined;
  }
};
